import React, { useEffect, useState } from 'react'
import { ResponsiveTableRow, H5, Subheading, Select } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import { timestampToDateString } from '../../../util/DateUtil'
import { getRecentlyResolvedMessages } from '../../../services/dmqueuesService'
import ObjectView from '../../common/ObjectView'
import useNotifications from '../../../hooks/useNotifications'
import useResponsiveTableExpandAll from '../../../hooks/useResponsiveTableExpandAll'

export default function RecentlyResolved() {
  usePageView({ page_detail: 'DMConsole:RecentlyResolvedV2' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.queues.recentlyResolved'))
  const [resolvedMessages, setResolvedMessages] = useState([])
  const [queueMap, setQueueMap] = useState({})
  const [selectedQueue, setSelectedQueue] = useState('all')
  const [resultsPerPage, setResultsPerPage] = useState('500')
  const { error } = useNotifications()
  const [rowExpanded, setRowExpanded] = useState({})
  const [allExpanded, setAllExpanded] = useState(false)
  const toggleExpandAll = () => {
    setRowExpanded(() => {
      const newExpandedState = {}
      resolvedMessages.forEach((message) => {
        newExpandedState[message.id] = !allExpanded
      })
      return newExpandedState
    })
    setAllExpanded((prev) => !prev)
  }
  useResponsiveTableExpandAll(toggleExpandAll, allExpanded)

  const handleExpandRow = (messageId) => {
    setRowExpanded((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }))
  }

  useEffect(() => {
    const queueName = selectedQueue === 'all' ? null : selectedQueue
    getRecentlyResolvedMessages(queueName, parseInt(resultsPerPage, 10))
      .then((res) => {
        setResolvedMessages(res.data.recents || [])
        setQueueMap(res.data.queueMap || {})
      })
      .catch(() => {
        error(t('queues.recentlyResolvedError', 'Error loading recently resolved messages'))
      })
  }, [error, resultsPerPage, selectedQueue, t])

  // Build options for Select
  const queueOptions = [
    { value: 'all', label: t('queues.all', 'All Queues') },
    ...Object.values(queueMap).map((queue) => ({ value: queue.name, label: queue.name })),
  ]

  const countOptions = ['10', '20', '50', '100', '200', '500'].map((count) => ({
    value: count,
    label: count,
  }))

  return (
    <>
      <Select
        id="queueSelect"
        label={t('queues.selectQueue', 'Select Queue')}
        value={selectedQueue}
        onChange={(e) => setSelectedQueue(e.target.value)}
        options={queueOptions}
        hidePlaceholder
        autoWidth
      />
      <Select
        id="countSelect"
        label={t('queues.resultsPerPage', 'Results Per Page')}
        value={resultsPerPage}
        onChange={(e) => setResultsPerPage(e.target.value)}
        options={countOptions}
        hidePlaceholder
        autoWidth
      />
      <H5 supplementary>
        {t('queues.recentlyResolved', 'Recently Resolved Messages')}
        <Subheading>{`${t('queues.total', {
          defaultValue: `${resolvedMessages.length} resolved messages`,
          total: resolvedMessages.length,
        })}`}</Subheading>
      </H5>
      {resolvedMessages.length > 0 ? (
        <DmcResponsiveTable
          totalColumnCount={3}
          atSizeColumns={{
            default: {
              labels: [t('resolvedAt', 'Resolved At'), t('queueName', 'Queue Name'), t('resolution', 'Resolution')],
              columnWidths: [4, 4, 4],
            },
          }}
          separator={<br />}
        >
          {resolvedMessages.map((message) => (
            <ResponsiveTableRow
              key={message.id}
              value={message.id}
              column1={timestampToDateString({
                timestamp: message.resolvedAt,
                withTime: true,
                timestampIsSeconds: true,
              })}
              column2={queueMap[message.queueId]?.name}
              column3={message.resolution}
              rowExpanded={rowExpanded[message.id]}
              setRowExpanded={() => handleExpandRow(message.id)}
            >
              <ObjectView object={message.details} noBorder />
            </ResponsiveTableRow>
          ))}
        </DmcResponsiveTable>
      ) : (
        <span>{t('queues.noRecentlyResolved', 'No recently resolved messages found.')}</span>
      )}
    </>
  )
}
